import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DeliveryMode } from '@spartacus/cart/base/root';
import { Address, CmsOrderDetailOverviewComponent, PaymentDetails, TranslationService } from '@spartacus/core';
import { OrderDetailsService, OrderOverviewComponent } from '@spartacus/order/components';
import { OrderHistory } from '@spartacus/order/root';
import { Card, CmsComponentData } from '@spartacus/storefront';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { addressFormatter } from '../../../../../custom/formatter/firstname-lastname-formatter';
import { ICON_TYPE } from "../../../../../custom/icons/icon.model";

@Component({
  selector: 'cx-order-overview',
  templateUrl: './custom-order-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomOrderOverviewComponent extends OrderOverviewComponent {

  @Input() isOrderHistoryList = false;
  @Input() order: OrderHistory;
  iconType = ICON_TYPE;
  component: any;

  constructor(
    protected translation: TranslationService,
    protected orderDetailsService: OrderDetailsService,
    protected componentData: CmsComponentData<CmsOrderDetailOverviewComponent>) {
    super(translation, orderDetailsService, componentData);
  }

  getReplenishmentCodeCardContent(orderCode: string): Observable<Card> {
    return this.translation
      .translate('checkoutOrderConfirmation.replenishmentNumber')
      .pipe(
        filter(() => Boolean(orderCode)),
        map((textTitle) => ({
          title: textTitle,
          text: [orderCode],
        }))
      );
  }

  getReplenishmentActiveCardContent(active: boolean): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutOrderConfirmation.status'),
      this.translation.translate('checkoutOrderConfirmation.active'),
      this.translation.translate('checkoutOrderConfirmation.cancelled'),
    ]).pipe(
      map(([textTitle, textActive, textCancelled]) => ({
        title: textTitle,
        text: [active ? textActive : textCancelled],
      }))
    );
  }


  getReplenishmentFrequencyCardContent(frequency: string): Observable<Card> {
    return this.translation
      .translate('checkoutOrderConfirmation.frequency')
      .pipe(
        filter(() => Boolean(frequency)),
        map((textTitle) => ({
          title: textTitle,
          text: [frequency],
        }))
      );
  }


  getOrderCodeCardContent(orderCode: string): Observable<Card> {
    return this.translation
      .translate('checkoutOrderConfirmation.orderNumber')
      .pipe(
        filter(() => Boolean(orderCode)),
        map((textTitle) => ({
          title: textTitle,
          text: [orderCode],
        }))
      );
  }


  getOrderStatusCardContent(status: string): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutOrderConfirmation.status'),
      this.translation.translate('orderDetails.statusDisplay', {
        context: status,
      }),
    ]).pipe(
      map(([textTitle, textStatus]) => ({
        title: textTitle,
        text: [textStatus],
      }))
    );
  }


  getMethodOfPaymentCardContent(
    hasPaymentInfo: PaymentDetails
  ): Observable<Card> {
    return combineLatest([
      this.translation.translate('orderDetails.paymentMethods.title'),
      this.translation.translate('orderDetails.paymentMethods.paymentType_ACCOUNT'),
      this.translation.translate('orderDetails.paymentMethods.paymentType_CREDIT_CARD'),
    ]).pipe(
      map(([textTitle, textAccount, textCard]) => ({
        textBold: textTitle,
        text: [Boolean(hasPaymentInfo) ? textCard : textAccount],
      }))
    );
  }


  getDeliveryModeCardContent(deliveryMode: DeliveryMode): Observable<Card> {
    return this.translation.translate('checkoutShipping.shippingMethod').pipe(
      filter(() => Boolean(deliveryMode)),
      map((textTitle) => ({
        title: textTitle,
        textBold: deliveryMode.name,
        text: [
          deliveryMode.description,
          deliveryMode.deliveryCost?.formattedValue
            ? deliveryMode.deliveryCost?.formattedValue
            : '',
        ],
      }))
    );
  }

  getAddressCardContent(deliveryAddress: Address): Observable<Card> {
    return this.translation.translate('orderDetails.deliveryAddress').pipe(
      map((textTitle) => ({
        title: '',
        textBold: textTitle,
        text: addressFormatter(deliveryAddress),
      }))
    );
  }

  getBillingAddressCardContent(billingAddress: Address): Observable<Card> {
    return this.translation.translate('orderDetails.billingAddress').pipe(
      map((textTitle) => ({
        title: '',
        textBold: textTitle,
        text: addressFormatter(billingAddress),
      }))
    );
  }

  getCarrierNameCardContent(carrierName): Observable<Card> {
    return this.translation.translate('orderDetails.consignmentTracking.dialog.carrier').pipe(
      map((textTitle) => ({
        title: '',
        textBold: textTitle,
        text: [carrierName],
      }))
    );
  }

  getCarrierIdentifierCardContent(receiverUid): Observable<Card> {
    return this.translation.translate('orderDetails.consignmentTracking.dialog.trackingId').pipe(
      map((textTitle) => ({
        title: '',
        textBold: textTitle,
        text: [receiverUid],
      }))
    );
  }

}
